import React, { useMemo } from 'react';
import { theme } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { getRoleTokenStyles } from './roleTokenStyles';

interface RoleBadgeTriggerProps {
  /** Preset colour name of the role (blue, green, purple, …). */
  color: string | undefined;
  label: React.ReactNode;
  onClick: () => void;
  /** Renders a plain badge without the edit affordance. */
  readOnly?: boolean;
}

/**
 * Role badge in the role's colour; clickable (with an edit icon and hover lift) unless read-only.
 */
export function RoleBadgeTrigger({ color, label, onClick, readOnly }: RoleBadgeTriggerProps) {
  const { token } = theme.useToken();
  const styles = useMemo(() => getRoleTokenStyles(color, token), [color, token]);

  const baseStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: token.marginXS,
    padding: `${token.paddingXXS}px ${token.paddingXS}px ${token.paddingXXS}px ${token.paddingSM}px`,
    background: styles.bg,
    border: `1px solid ${styles.border}`,
    borderRadius: token.borderRadius,
    fontSize: 13,
    fontWeight: 500,
    color: styles.text
  };

  if (readOnly) {
    return (
      <div style={baseStyle}>
        <span>{label}</span>
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      style={{ ...baseStyle, cursor: 'pointer', transition: 'all 0.2s ease' }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = token.boxShadowSecondary;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <span>{label}</span>
      <EditOutlined style={{ fontSize: 12, opacity: 0.7, color: styles.icon }} />
    </div>
  );
}
